<script setup lang="ts">
const wrapper = ref<HTMLElement | null>(null)
let initialHeight = 0
let drag: { pointerId: number; startY: number; startHeight: number; minHeight: number; textarea: HTMLTextAreaElement } | null = null

onMounted(() => {
  initialHeight = wrapper.value?.querySelector('textarea')?.getBoundingClientRect().height || 0
})

// getMinHeight 读取文本框的最小高度
const getMinHeight = (textarea: HTMLTextAreaElement) => {
  return parseFloat(getComputedStyle(textarea).minHeight) || initialHeight
}

// startResize 捕获指针并记录拖动起点
const startResize = (event: PointerEvent) => {
  if (!event.isPrimary || event.button !== 0) return
  const textarea = wrapper.value?.querySelector('textarea')
  if (!textarea || textarea.disabled || textarea.readOnly) return
  event.preventDefault()
  drag = {
    pointerId: event.pointerId,
    startY: event.clientY,
    startHeight: textarea.getBoundingClientRect().height,
    minHeight: getMinHeight(textarea),
    textarea
  }
  const handle = event.currentTarget as HTMLElement
  handle.setPointerCapture(event.pointerId)
}

// resize 根据纵向位移调整高度
const resize = (event: PointerEvent) => {
  if (!drag || event.pointerId !== drag.pointerId) return
  drag.textarea.style.height = `${Math.max(drag.minHeight, drag.startHeight + event.clientY - drag.startY)}px`
}

// stopResize 结束当前拖动
const stopResize = () => {
  drag = null
}

// resizeWithKeyboard 使用方向键调整高度
const resizeWithKeyboard = (event: KeyboardEvent) => {
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
  const textarea = wrapper.value?.querySelector('textarea')
  if (!textarea || textarea.disabled || textarea.readOnly) return
  event.preventDefault()
  const height = textarea.getBoundingClientRect().height + (event.key === 'ArrowUp' ? -16 : 16)
  textarea.style.height = `${Math.max(getMinHeight(textarea), height)}px`
}
</script>

<template>
  <div ref="wrapper" class="resizable-textarea">
    <slot />
    <button
      class="resize-handle"
      type="button"
      aria-label="调整文本框高度"
      title="拖动或使用上下方向键调整高度"
      @pointerdown="startResize"
      @pointermove="resize"
      @pointerup="stopResize"
      @pointercancel="stopResize"
      @lostpointercapture="stopResize"
      @keydown="resizeWithKeyboard"
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 21h3a9 9 0 0 0 9-9V9" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.resizable-textarea {
  position: relative;
  min-width: 0;
}

.resizable-textarea :slotted(textarea) {
  display: block;
  resize: none;
}

.resize-handle {
  position: absolute;
  right: 3px;
  bottom: 3px;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--text-soft);
  cursor: ns-resize;
  touch-action: none;
}

.resize-handle svg {
  width: 24px;
  height: 24px;
  opacity: 0.45;
}

.resize-handle:hover svg,
.resize-handle:focus-visible svg {
  opacity: 0.8;
}
</style>
